<script setup>
import { ref, onMounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import MiniStatisticsCard from "@/examples/MiniStatisticsCard.vue";
import { StudentsClient } from "@/services/StudentsClient";

const router = useRouter();
const courses = ref([]);
const isLoading = ref(true);
const error = ref(null);
const studentsClient = new StudentsClient();

// Transform API data to match our component structure
const transformCourseData = (apiCourses) => {
  return apiCourses.map(course => {
    const quizzes = course.quices.map(quiz => {
      const [correct, total] = quiz.resumen_preguntas.split('/').map(Number);

      return {
        id: quiz.quizid,
        name: quiz.quiz_nombre,
        grade: quiz.calificacion !== null ? Math.round(quiz.calificacion) : "N/A",
        correct: correct || 0,
        total: total || 1, // Avoid division by zero
        submitted: quiz.calificacion !== null ? "Entregado" : "No entregado",
        chapter_id: quiz.capitulo?.capitulo_id
      };
    });

    return {
      id: course.curso_id,
      name: course.curso_titulo,
      code: `CRS${course.curso_id}`,
      totalGrade: course.nota ? Math.round(parseFloat(course.nota)) : 0,
      isOpen: false,
      label: course.categoria_nombre,
      date: course.fecha_inscripcion,
      chapters: `${quizzes.length} Evaluaciones`,
      quizzes: quizzes,
      instructor: course.profesor_nombre,
      instructor_mail: course.profesor_correo,

      percentageCompleted: parseFloat(course.porcentaje_completado) || 0
    };
  });
};

// Fetch student's courses with grades
const fetchStudentCourses = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await studentsClient.getProfile();
    console.log(response);

    if (!response || !Array.isArray(response)) {
      throw new Error('Invalid data format received');
    }

    courses.value = transformCourseData(response);
    console.log(courses.value);

  } catch (err) {
    error.value = err.message || "Failed to load your courses";
    console.error("Error fetching student courses:", err);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchStudentCourses();
  nextTick(() => {
    // Ensure the page is scrolled to the top after loading
    window.scrollTo(0, 0);
  });
});

const navigateToCourse = (id, event) => {
  // Prevent the toggle action if clicking the course name link
  event.stopPropagation();
  router.push({ path: `/cursos/${id}` });
};

const toggleCourse = (courseId, event) => {
  // Only toggle if not clicking on a link or button
  if (event && (event.target.tagName === 'A' || event.target.closest('a') || event.target.tagName === 'BUTTON')) {
    return;
  }
  const course = courses.value.find(c => c.id === courseId);
  if (course) course.isOpen = !course.isOpen;
};

const calculateAverage = (gradesArray) => {
  if (!gradesArray || gradesArray.length === 0) return 0;
  const sum = gradesArray.reduce((acc, grade) => acc + grade, 0);
  return Math.round(sum / gradesArray.length);
};

const formatDate = (dateString) => {
  if (!dateString) return 'Fecha no disponible';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('es-ES', options);
};

const navigateToQuiz = (courseId, chapterId, quizId) => {
  console.log(`Navigating to Quiz: Course ID: ${courseId}, Chapter ID: ${chapterId}, Quiz ID: ${quizId}`);
  router.push({
    name: "Quiz View",
    params: {
      curso: courseId,
      capitulo: chapterId,
      quiz: quizId
    }
  });
};
</script>

<template>
  <div class="p-3 py-md-4 p-lg-4 container-fluid">
    <div class="col-12">
      <!-- Loading state -->
      <div v-if="isLoading" class="text-center py-10 my-10 ">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
        <p class="mt-2">Cargando Cursos...</p>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="alert alert-danger">
        Error loading grades: {{ error }}
      </div>

      <!-- Main content -->
      <template v-else>
        <!-- Statistics Cards -->
        <div class="row">
          <div class="mb-1 mb-lg-4 col-lg-6 col-6 mb-xl-0">
            <mini-statistics-card title="Promedio General"
              :value="calculateAverage(courses.map(c => c.totalGrade)) + '%'" :icon="{
                component: 'fas fa-chart-line text-info',
                background: 'bg-light',
              }" />
          </div>
          <div class="mb-1 mb-lg-4 col-lg-6 col-6 mb-xl-0">
            <mini-statistics-card title="Cursos Activos" :value="courses.length + ' Cursos'" :icon="{
              component: 'fas fa-book-open text-success',
              background: 'bg-light',
            }" />
          </div>
        </div>

        <!-- Courses List -->
        <div class="mb-4 card">
          <div class="p-3 pb-0 card-header">
            <h6 class="mb-1">Mis Cursos</h6>
            <p class="text-sm">Despliega cada curso para ver detalles de tus calificaciones</p>
          </div>

          <div class="p-3 card-body">
            <div class="accordion" id="gradesAccordion"
              style="max-height: 150vh; min-height: 65vh; overflow-y: auto; overflow-x: hidden;">
              <!-- Course Loop -->
              <div v-for="course in courses" :key="course.id" class="mb-4 course card shadow-sm "
                @click="toggleCourse(course.id, $event)">
                <div class="p-3 card-header cursor-pointer" :class="course.isOpen ? 'bg-gray-300' : 'bg-gray-200'">

                  <!-- Mobile Layout (stacked) -->
                  <div class="d-flex flex-column d-lg-none">
                    <!-- Row 1: Label -->
                    <div class="mb-2">
                      <span class="badge bg-info">{{ course.label }}</span>
                    </div>

                    <!-- Row 2: Course Name -->
                    <div class="mb-2">
                      <h5 class="mb-0 course-name">
                        <a href="#" @click="navigateToCourse(course.id, $event)" class="text-dark text-decoration-none">
                          {{ course.name }}
                        </a>
                      </h5>
                    </div>

                    <!-- Row 3: Progress Bar -->
                    <div class="mb-3">
                      <div class="progress w-100 mb-1" style="height: 8px;">
                        <div class="progress-bar bg-success" role="progressbar"
                          :style="{ width: course.percentageCompleted + '%' }"
                          :aria-valuenow="course.percentageCompleted" aria-valuemin="0" aria-valuemax="100">
                        </div>
                      </div>
                      <small class="text-xs">{{ course.percentageCompleted }}% completado</small>
                    </div>

                    <!-- Row 4: Instructor Info -->
                    <div class="mb-2">
                      <span class="text-sm"><i class="fas fa-chalkboard-teacher pe-2"></i>{{ course.instructor }}</span>
                      <p class="mt-1 mb-0 text-xs"><i class="fas fa-envelope pe-2 ps-1"></i>{{ course.instructor_mail }}
                      </p>
                    </div>

                    <!-- Row 5: Date and Grade -->
                    <div class="d-flex justify-content-between align-items-center">
                      <p class="mb-0 text-xxs">Matriculado el: {{ formatDate(course.date) }}</p>
                      <div class="d-flex align-items-center">
                        <p class="me-2 mb-0 text-sm">Calif: </p>
                        <span class="badge me-2" :class="{
                          'bg-success': course.totalGrade >= 70,
                          'bg-danger': course.totalGrade < 70
                        }">
                          {{ course.totalGrade }}%
                        </span>
                        <button class="btn btn-link mb-0 p-0" @click.stop="toggleCourse(course.id)"
                          aria-label="Toggle course details">
                          <i class="fas fa-chevron-down transition-all" :class="{ 'rotate-180': course.isOpen }"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Desktop Layout (original horizontal) -->
                  <div class="d-none d-lg-flex justify-content-between align-items-center">
                    <!-- First section: Course info (40% width) -->
                    <div class="w-40 pe-3">
                      <div class="d-flex align-items-center mt-1 mb-2">
                        <h5 class="mb-0 me-3 course-name">
                          <a href="#" @click="navigateToCourse(course.id, $event)"
                            class="text-dark text-decoration-none">
                            {{ course.name }}
                          </a>
                        </h5>
                        <span class="badge bg-info">{{ course.label }}</span>
                      </div>
                      <span class="text-md mt-3"><i class="fas fa-chalkboard-teacher pe-2"></i> {{ course.instructor
                        }}</span>
                      <p class="mt-1 mb-0 text-sm"><i class="fas fa-envelope pe-2 ps-1"></i>{{ course.instructor_mail }}
                      </p>
                      <p class="mt-3 mb-0 text-xxs">Matriculado el: {{ formatDate(course.date) }}</p>
                    </div>

                    <!-- Second section: Progress bar (30% width) -->
                    <div class="w-30 px-3 d-flex flex-column align-items-center">
                      <div class="progress w-100 mb-2" style="height: 10px;">
                        <div class="progress-bar bg-success" role="progressbar"
                          :style="{ width: course.percentageCompleted + '%' }"
                          :aria-valuenow="course.percentageCompleted" aria-valuemin="0" aria-valuemax="100">
                        </div>
                      </div>
                      <small class="text-xs">{{ course.percentageCompleted }}% completado</small>
                    </div>

                    <!-- Third section: Grade and toggle (30% width) -->
                    <div class="w-30 ps-3 d-flex align-items-center justify-content-end">
                      <p class="me-2 mb-0 text-md">Calificación: </p>
                      <span class="badge me-3" :class="{
                        'bg-success': course.totalGrade >= 70,
                        'bg-danger': course.totalGrade < 70
                      }">
                        {{ course.totalGrade }}%
                      </span>
                      <button class="btn btn-link mb-0 p-0" @click.stop="toggleCourse(course.id)"
                        aria-label="Toggle course details">
                        <i class="fas fa-chevron-down transition-all" :class="{ 'rotate-180': course.isOpen }"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <transition name="expand">
                  <div v-if="course.isOpen" class="card-body">
                    <div class="table-responsive">
                      <table class="table align-items-center mb-0">
                        <thead>
                          <tr>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Evaluación
                            </th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                              Resultado</th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                              Calificación</th>
                            <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                              Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="quiz in course.quizzes" :key="quiz.id">
                            <td>
                              <p class="text-xs font-weight-bold mb-0">
                                <a href="#" @click.prevent="navigateToQuiz(course.id, quiz.chapter_id, quiz.id)"
                                  class="text-decoration-none">
                                  {{ quiz.name }}
                                </a>
                              </p>
                            </td>
                            <td>
                              <p class="text-xs font-weight-bold mb-0">
                                {{ quiz.correct }}/{{ quiz.total }} correctas
                              </p>
                            </td>
                            <td class="align-middle text-center">
                              <span class="badge badge-sm" :class="{
                                'bg-success': quiz.grade >= 70,
                                'bg-danger': quiz.grade < 70 && quiz.grade !== '-',
                              }">
                                {{ quiz.grade }}%
                              </span>
                            </td>
                            <td class="align-middle text-center">
                              <span class="text-xs">{{ quiz.submitted }}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="text-end mt-3">
                      <button class="btn btn-sm btn-outline-primary mb-0" @click="navigateToCourse(course.id, $event)">
                        Ir al curso
                      </button>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.rotate-180 {
  transform: rotate(180deg);
}

.transition-all {
  transition: all 0.3s ease;
}

.course:hover {
  transform: scale(1.005);
  transition: all 0.3s ease-in-out;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

a.text-dark:hover {
  color: var(--bs-primary) !important;
}

.progress {
  min-width: 300px;
  background-color: #c4c8cb;
}

.course-name:hover {
  text-decoration: underline;

  transform: scale(1.030);
  transition: all 0.3s ease-in-out;
}

a {
  color: inherit;
  transition: color 0.2s ease;
}

a:hover {
  color: #2dce89;
  text-decoration: underline;
}
@media screen and (max-width: 768px) {
  .course-name {
    font-size: 1rem;
  }
}
</style>